import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import Layout from '@/layouts/index.vue'

Vue.use(VueRouter)

// ==================== 修复 vue-router 3.1+ 导航重复/重定向报错 ====================
const originalPush = VueRouter.prototype.push
const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(() => { })
}
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(() => { })
}

// ==================== 静态路由 ====================
export const staticRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/view/login/index.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/layouts/index.vue'),
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/view/home/index.vue'),
        meta: { title: '首页', breadcrumb: ['首页'] }
      },
      {
        // 个人中心：登录后即可访问，无需后端菜单/权限，走静态路由
        path: 'profile',
        name: 'Profile',
        component: () => import('@/view/profile/index.vue'),
        meta: { title: '个人中心', breadcrumb: ['个人中心'] }
      }
    ]
  }
]

// ==================== 动态路由生成 ====================
// 遍历菜单树，为每个 type=10（菜单）或 type=20（子页面）的节点生成一条子路由
// type=10：侧边栏可见的菜单页（如角色管理）
// type=20：子页面（如编辑页），不显示在侧边栏但需要正常加载路由
// type=30：按钮权限，后端已过滤，不参与路由生成
// 递归过程中顺手把父级 name 链算好塞进 meta.breadcrumb，面包屑组件直接读取即可
function generateDynamicRoutes(menus) {
  const routes = []

  // parents: 当前节点向上到顶的 name 链（不含自身）
  function walk(list, parents) {
    if (!Array.isArray(list)) return
    list.forEach(item => {
      // 跳过按钮（type=30）
      if (item.type === 30) return

      // 当前节点的完整面包屑链 = 父级链 + 自身 name
      const breadcrumb = [...parents, item.name]

      // type=10 / type=20 需要生成路由
      if ((item.type === 10 || item.type === 20) && item.path) {
        // 使用后端返回的 component 字段动态加载
        if (item.component) {
          const component = () => import(`@/view/${item.component}.vue`)
          routes.push({
            path: item.path,           // 以 / 开头的绝对路径，作为 Layout 子路由
            name: item.code || item.path,
            component,
            meta: {
              title: item.name,
              icon: item.icon,
              type: item.type,         // 标记节点类型：10=菜单 20=子页面
              breadcrumb               // 预计算的面包屑链路
            }
          })
        }
      }
      // 递归处理子节点，把当前面包屑链传下去
      if (item.children && item.children.length) {
        walk(item.children, breadcrumb)
      }
    })
  }

  walk(menus, [])
  return routes
}

const router = new VueRouter({
  routes: staticRoutes
})

// ==================== 路由守卫 ====================
// 首次进入 / 刷新页面时，根据 vuex 中的 menus 动态添加路由
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token')

  // 未登录 → 登录页
  if (!token) {
    if (to.path === '/login') {
      next()
    } else {
      next('/login')
    }
    return
  }

  // 已登录访问登录页 → 重定向到首页
  if (to.path === '/login') {
    next('/')
    return
  }

  // 动态路由尚未生成 → 先生成（menus 为空就按空生成，无动态路由）
  if (!store.state.user.hasRoutes) {
    const dynamicRoutes = generateDynamicRoutes(store.state.user.menus)
    // 将动态路由作为 Layout 的 children 添加（父记录必须有 component，子组件才能渲染）
    // 同时把 404 catch-all 放在动态路由之后，确保动态路由优先于 * 被匹配
    router.addRoutes([
      {
        path: '/',
        component: Layout,
        children: dynamicRoutes
      },
      {
        path: '*',
        name: '404',
        component: () => import('@/view/404.vue')
      }
    ])
    store.commit('user/SET_HAS_ROUTES', true)

    // addRoutes 后用 replace 重新匹配，避免 404
    next({ ...to, replace: true })
    return
  }

  next()
})

export default router
